/**
 * HI-SI Dashboard - Individual Execution Detail Page
 * Enhanced with real-time iteration visibility (FIX-004)
 */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  AlertTriangle
} from 'lucide-react';
import PipelineTimeline from '@/components/pipeline/PipelineTimeline';
import { StageType } from '@/types/execution';

const ExecutionPage: React.FC = () => {
  const { executionId } = useParams<{ executionId: string }>();
  const [selectedStage, setSelectedStage] = useState<StageType | null>(null);
  
  if (!executionId) {
    return (
      <div className="text-center py-12">
        <div className="flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Invalid Execution ID
        </h2>
        <p className="text-gray-600 mb-6">
          Please provide a valid execution ID to view the pipeline execution.
        </p>
        <Link to={(window.location.pathname.startsWith('/dashboard') ? '/dashboard' : '') + '/executions'} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Executions
        </Link>
      </div>
    );
  }

  const handleStageClick = (stage: StageType) => {
    setSelectedStage(stage);
    // TODO: Could open a detailed stage view or modal
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link 
            to={(window.location.pathname.startsWith('/dashboard') ? '/dashboard' : '') + '/executions'} 
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Pipeline Execution
            </h1>
            <p className="text-sm text-gray-600">
              Real-time iteration monitoring • ID: {executionId.slice(0, 8)}
            </p>
          </div>
        </div>
      </div>

      {/* FIX-004 Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-blue-500 mr-3" />
          <div>
            <h3 className="text-blue-800 font-medium">FIX-004: New Iteration UI Active</h3>
            <p className="text-blue-600 text-sm mt-1">
              This page now shows real-time iteration monitoring. Database connection will be attempted, falling back to demo data if needed.
            </p>
          </div>
        </div>
      </div>

      {/* Main Execution View */}
      <PipelineTimeline
        execution={{ id: executionId } as any}
        onStageClick={handleStageClick}
      />

      {/* Stage Details Modal/Panel could go here */}
      {selectedStage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-6xl max-h-[90vh] overflow-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {selectedStage} Stage Details
              </h2>
              <button
                onClick={() => setSelectedStage(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            </div>
            <div className="text-gray-600">
              <p>Detailed stage view for {selectedStage} would be implemented here.</p>
              <p className="mt-2 text-sm">This could show:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li>All iterations for this stage in a grid view</li>
                <li>Detailed quality analysis and scores</li>
                <li>Cost breakdown by iteration</li>
                <li>Comparison tools between iterations</li>
                <li>Full content preview with media files</li>
                <li>Export and sharing options</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutionPage;